// Multi-Zones: navigating between the marketing/portal/dashboard/auth apps is
// always a hard page load (separate deployments), never client-side routing -
// these constants are the one place each zone's base URL is defined. Dev runs
// each zone on its own port; production path-prefixes them under one domain
// via nginx, so the two environments resolve to genuinely different values -
// hence the env var override rather than a hardcoded path string at every
// call site (a bare '/portal' string worked by accident under the old
// single-app router, but router.push/replace doesn't cross zone boundaries).
export const MARKETING_ZONE_URL = process.env.NEXT_PUBLIC_MARKETING_ZONE_URL ?? 'http://localhost:3000';
export const PORTAL_ZONE_URL = process.env.NEXT_PUBLIC_PORTAL_ZONE_URL ?? 'http://localhost:3001';
export const DASHBOARD_ZONE_URL = process.env.NEXT_PUBLIC_DASHBOARD_ZONE_URL ?? 'http://localhost:3002';
export const AUTH_ZONE_URL = process.env.NEXT_PUBLIC_AUTH_ZONE_URL ?? 'http://localhost:3003';

// ── Canonical site URL & production detection (root layout metadata) ─────
// Every app's root layout needs an absolute URL for Next's metadataBase and
// a way to gate prod-only metadata (e.g. robots indexing) - was hand-copied
// per app instead of shared.
export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://elitescholars.co.za';
export const IS_PRODUCTION_SITE = process.env.NEXT_PUBLIC_SITE_URL === 'https://elitescholars.co.za';

// ── Post-login return URLs ────────────────────────────────────────────────
// A guard sends a signed-out visitor to /auth/login with ?returnUrl=<where
// they were headed> so the deep link survives the round trip - without it,
// every deep link into the portal dumps the user on their dashboard and
// silently discards whatever the link carried (query params included).
//
// SECURITY: a redirect target taken from a query param is an open-redirect
// hole unless it is validated. Only two things are ever accepted: a
// same-site relative path, or an absolute URL whose ORIGIN is one of our
// own zones. Anything else - another host, a protocol-relative '//evil',
// a javascript: URL - returns null and the caller falls back to its normal
// landing.
export const RETURN_URL_PARAM = 'returnUrl';

// ── Auth zone's own internal routes ───────────────────────────────────────
// Bare paths for use inside apps/auth itself - router.push/replace/redirect
// stay within the zone, so basePath '/auth' is applied automatically (see
// apps/auth/next.config.js). Also the source every other zone builds
// fully-qualified auth links from via authZoneHref(), so a route is spelled
// in exactly one place rather than hand-copied at every call site (one such
// copy had already dropped the AUTH_ZONE_URL prefix entirely - see
// not-found.tsx in portal/dashboard, fixed alongside this).
export const AUTH_ROUTES = {
  login: '/login',
  register: '/register',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  changePassword: '/change-password',
  emailVerification: '/email-verification',
  emailAddressVerification: '/email-address-verification',
  continue: '/continue',
  onboarding: '/onboarding',
  onboardingStudent: '/onboarding/student',
  onboardingParent: '/onboarding/parent'
} as const;

// Where Continue goes after an email action link (/auth/email-address-verification).
// A verified email continues into the platform through /continue, which already
// knows what is next (sign in, onboarding, or the user's portal/dashboard). It went
// to the marketing home page instead (Governor, 2026-10-05). Changing or restoring
// the sign-in email ends existing sessions, so those go to sign-in. Both are
// in-zone routes: the caller navigates with the auth zone's router, which adds
// the /auth basePath (a bare window.location '/login' landed on a marketing 404).
export function routeAfterEmailLink(mode: string): string {

  return mode === 'verifyEmail' ? AUTH_ROUTES.continue : AUTH_ROUTES.login;
}

// For portal/dashboard/marketing: composes a fully-qualified link into the
// auth zone (AUTH_ZONE_URL + the /auth basePath + the route) from the same
// AUTH_ROUTES constants apps/auth uses internally.
export function authZoneHref(route: string): string {

  return `${ AUTH_ZONE_URL }/auth${ route }`;
}

export function safeReturnUrl(raw: string | null | undefined): string | null {

  if (!raw) {

    return null;
  }

  // '//evil.com' is protocol-relative: it LOOKS like a path but the browser
  // treats it as another origin, so reject it before the relative-path case.
  // '/\evil.com' is the same thing: browsers read a backslash as a slash.
  if (raw.startsWith('//') || raw.startsWith('/\\')) {

    return null;
  }

  if (raw.startsWith('/')) {

    return raw;
  }

  try {

    const target = new URL(raw);
    const allowed = [ MARKETING_ZONE_URL, PORTAL_ZONE_URL, DASHBOARD_ZONE_URL, AUTH_ZONE_URL ]
      .map((zone) => {

        try {

          return new URL(zone).origin;
        } catch {

          return null;
        }
      })
      .filter((origin): origin is string => origin !== null);

    return allowed.includes(target.origin) ? target.toString() : null;
  } catch {

    return null;
  }
}

// An auth-zone route with a validated returnUrl attached (login -> register
// keeps the deep link in the URL, not only in localStorage). Anything
// safeReturnUrl rejects is dropped and the bare route returned.
export function withReturnUrl(route: string, raw: string | null | undefined): string {

  const safe = safeReturnUrl(raw);

  return safe ? `${ route }?${ RETURN_URL_PARAM }=${ encodeURIComponent(safe) }` : route;
}
